import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BookCoverUploader } from './BookCoverUploader';
import { booksManageService } from '../../../api/booksManageService';
import { useBookQuery } from '../../../hooks/useBookQuery';
import type { BookDto } from '../../../types/book';
import type {
  BookStatus,
  UpdateBookManageDto,
} from '../../../types/manageBooks';
import { EpubRendererType } from '../../../types/manageBooks';
import { useTranslation } from '../../../i18n/useTranslation';

type Tab = 'metadata' | 'drm' | 'cover';

interface Props {
  bookId: string | null;
  publishers: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
  /** True when the current user holds learnleaf.App.SwitchBookRenderer. */
  canSwitchRenderer: boolean;
  /** Called with the bookId when the user clicks "Switch renderer…". */
  onRequestRendererSwitch: (bookId: string) => void;
}

interface DrmFormState {
  defaultPrintQuotaLimit: string;
  defaultPrintQuotaWindowDays: string;
  defaultCopyQuotaLimit: string;
  defaultCopyQuotaWindowDays: string;
  defaultDownloadQuotaLimit: string;
  defaultDownloadQuotaWindowDays: string;
}

const EMPTY_DRM: DrmFormState = {
  defaultPrintQuotaLimit: '',
  defaultPrintQuotaWindowDays: '',
  defaultCopyQuotaLimit: '',
  defaultCopyQuotaWindowDays: '',
  defaultDownloadQuotaLimit: '',
  defaultDownloadQuotaWindowDays: '',
};

function parseOptional(v: string): number | undefined {
  if (v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function fromNullableNumber(n: number | null | undefined): string {
  return n === null || n === undefined ? '' : String(n);
}

export function BookEditDrawer({
  bookId,
  publishers,
  onClose,
  onSaved,
  canSwitchRenderer,
  onRequestRendererSwitch,
}: Props) {
  const { t } = useTranslation();
  const { data: book, isLoading, error } = useBookQuery(bookId);
  const [tab, setTab] = useState<Tab>('metadata');
  const [form, setForm] = useState<UpdateBookManageDto | null>(null);
  const [drm, setDrm] = useState<DrmFormState>(EMPTY_DRM);
  const [dirty, setDirty] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    if (!book) return;
    setForm({
      title: book.title ?? '',
      author: book.author ?? '',
      isbn: book.isbn ?? undefined,
      publisherId: book.publisherId,
      status: (book.status as BookStatus) ?? 1,
      subjects: book.subjects ?? undefined,
      contributors: book.contributors ?? undefined,
      rights: book.rights ?? undefined,

      complexityScore: book.complexityScore ?? undefined,
      isDrmEnabled: !!book.isDrmEnabled,
      coverImageUrl: book.coverImageUrl ?? undefined,
      concurrencyStamp: book.concurrencyStamp ?? '',
    });
    setDrm({
      defaultPrintQuotaLimit:        fromNullableNumber(book.defaultPrintQuotaLimit),
      defaultPrintQuotaWindowDays:   fromNullableNumber(book.defaultPrintQuotaWindowDays),
      defaultCopyQuotaLimit:         fromNullableNumber(book.defaultCopyQuotaLimit),
      defaultCopyQuotaWindowDays:    fromNullableNumber(book.defaultCopyQuotaWindowDays),
      defaultDownloadQuotaLimit:     fromNullableNumber(book.defaultDownloadQuotaLimit),
      defaultDownloadQuotaWindowDays:fromNullableNumber(book.defaultDownloadQuotaWindowDays),
    });
    setDirty(false);
  }, [book]);

  const saveMetadata = useMutation({
    mutationFn: (payload: UpdateBookManageDto) => booksManageService.update(bookId!, payload),
    onSuccess: (updated: BookDto) => {
      qc.setQueryData(['manage-book', bookId], updated);
      void qc.invalidateQueries({ queryKey: ['manage-books'] });
      setDirty(false);
      onSaved();
    },
  });

  const uploadCover = useMutation({
    mutationFn: (file: File) => booksManageService.replaceCover(bookId!, file),
    onSuccess: (updated: BookDto) => {
      qc.setQueryData(['manage-book', bookId], updated);
      void qc.invalidateQueries({ queryKey: ['manage-books'] });
      setForm(f => f ? { ...f, coverImageUrl: updated.coverImageUrl ?? undefined } : f);
    },
  });

  const restoreBook = useMutation({
    mutationFn: () => booksManageService.restore(bookId!),
    onSuccess: (updated: BookDto) => {
      qc.setQueryData(['manage-book', bookId], updated);
      void qc.invalidateQueries({ queryKey: ['manage-books'] });
      setForm(f => f ? { ...f } : f); // force re-render of banner
      onSaved();
    },
  });

  const saveDrm = useMutation({
    mutationFn: () => booksManageService.bulkQuotas({
      bookIds: [bookId!],
      isDrmEnabled: form?.isDrmEnabled,
      defaultPrintQuotaLimit:        parseOptional(drm.defaultPrintQuotaLimit),
      defaultPrintQuotaWindowDays:   parseOptional(drm.defaultPrintQuotaWindowDays),
      defaultCopyQuotaLimit:         parseOptional(drm.defaultCopyQuotaLimit),
      defaultCopyQuotaWindowDays:    parseOptional(drm.defaultCopyQuotaWindowDays),
      defaultDownloadQuotaLimit:     parseOptional(drm.defaultDownloadQuotaLimit),
      defaultDownloadQuotaWindowDays:parseOptional(drm.defaultDownloadQuotaWindowDays),
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['manage-book', bookId] });
      void qc.invalidateQueries({ queryKey: ['manage-books'] });
      setDirty(false);
      onSaved();
    },
  });

  const attemptClose = () => {
    if (!dirty || window.confirm(t('App.Admin.Books.BookEditDrawer.DiscardConfirm'))) onClose();
  };

  if (!bookId) return null;

  const tabLabel = (tabKey: Tab): string => {
    if (tabKey === 'drm') return t('App.Admin.Books.BookEditDrawer.TabDrm');
    if (tabKey === 'metadata') return t('App.Admin.Books.BookEditDrawer.TabMetadata');
    return t('App.Admin.Books.BookEditDrawer.TabCover');
  };

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label={t('App.Admin.Books.BookEditDrawer.AriaLabel')}>
      <div className="absolute inset-0 bg-black/30" onClick={attemptClose} />
      <aside className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-xl flex flex-col">
        <header className="border-b border-neutral-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold truncate">
            {book?.title ?? (isLoading ? t('App.Admin.Books.BookEditDrawer.LoadingTitle') : t('App.Admin.Books.BookEditDrawer.DefaultTitle'))}
          </h2>
          <button
            type="button"
            onClick={attemptClose}
            className="text-neutral-500 hover:text-neutral-900 min-h-[44px] min-w-[44px]"
            aria-label={t('App.Admin.Books.BookEditDrawer.CloseAriaLabel')}
          >✕</button>
        </header>

        {book?.isDeleted && (
          <div
            role="alert"
            className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 flex items-center justify-between gap-3"
          >
            <div className="text-sm">
              {t('App.Admin.Books.BookEditDrawer.DeletedBanner')}
            </div>
            <button
              type="button"
              onClick={() => restoreBook.mutate()}
              disabled={restoreBook.isPending}
              className="px-3 py-1 bg-red-600 text-white rounded text-sm min-h-[36px] disabled:opacity-50"
            >
              {restoreBook.isPending ? t('App.Admin.Books.BookEditDrawer.Restoring') : t('App.Admin.Books.BookEditDrawer.RestoreButton')}
            </button>
          </div>
        )}
        {restoreBook.isError && (
          <p role="alert" className="text-sm text-red-600 px-4 py-2">
            {(restoreBook.error as Error).message}
          </p>
        )}

        <nav className="flex border-b border-neutral-200" role="tablist">
          {(['metadata', 'drm', 'cover'] as Tab[]).map(tabKey => (
            <button
              key={tabKey}
              type="button"
              onClick={() => setTab(tabKey)}
              className={`px-4 py-2 text-sm capitalize min-h-[44px] ${tab === tabKey ? 'border-b-2 border-primary-600 text-primary-600' : 'text-neutral-600'}`}
              role="tab"
              aria-selected={tab === tabKey}
            >
              {tabLabel(tabKey)}
            </button>
          ))}
        </nav>

        <div className="flex-1 overflow-auto p-4">
          {error && (
            <p role="alert" className="text-sm text-red-600 mb-3">
              {t('App.Admin.Books.BookEditDrawer.LoadError', { error: (error as Error).message })}
            </p>
          )}

          {isLoading || !form ? <p className="text-sm text-neutral-500">{t('App.Admin.Books.BookEditDrawer.LoadingContent')}</p> : (
            <>
              {tab === 'metadata' && (
                <div className="space-y-3" role="tabpanel">
                  <Field label={t('App.Admin.Books.BookEditDrawer.FieldTitle')}>
                    <input
                      className="border rounded px-3 py-2 min-h-[44px] w-full"
                      value={form.title}
                      onChange={e => { setForm({ ...form, title: e.target.value }); setDirty(true); }}
                    />
                  </Field>
                  <Field label={t('App.Admin.Books.BookEditDrawer.FieldAuthor')}>
                    <input
                      className="border rounded px-3 py-2 min-h-[44px] w-full"
                      value={form.author}
                      onChange={e => { setForm({ ...form, author: e.target.value }); setDirty(true); }}
                    />
                  </Field>
                  <Field label={t('App.Admin.Books.BookEditDrawer.FieldISBN')}>
                    <input
                      className="border rounded px-3 py-2 min-h-[44px] w-full"
                      value={form.isbn ?? ''}
                      onChange={e => { setForm({ ...form, isbn: e.target.value || undefined }); setDirty(true); }}
                    />
                  </Field>
                  <Field label={t('App.Admin.Books.BookEditDrawer.FieldPublisher')}>
                    <select
                      className="border rounded px-3 py-2 min-h-[44px] w-full"
                      value={form.publisherId}
                      onChange={e => { setForm({ ...form, publisherId: e.target.value }); setDirty(true); }}
                    >
                      {publishers.length === 0 && (
                        <option value={form.publisherId}>{form.publisherId.slice(0, 8)}&hellip; {t('App.Admin.Books.BookEditDrawer.FieldPublisherCurrentHint')}</option>
                      )}
                      {publishers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </Field>
                  <Field label={t('App.Admin.Books.BookEditDrawer.FieldStatus')}>
                    <select
                      className="border rounded px-3 py-2 min-h-[44px] w-full"
                      value={form.status}
                      onChange={e => { setForm({ ...form, status: Number(e.target.value) as BookStatus }); setDirty(true); }}
                    >
                      <option value={1}>{t('App.Common.BookStatus.Draft')}</option>
                      <option value={2}>{t('App.Common.BookStatus.Published')}</option>
                      <option value={3}>{t('App.Common.BookStatus.Archived')}</option>
                    </select>
                  </Field>
                  <Field label={t('App.Admin.Books.BookEditDrawer.FieldRendererType')}>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-neutral-800">
                        {(book?.rendererType as EpubRendererType) === EpubRendererType.EpubJs ? t('App.Common.RendererType.EpubJs') : t('App.Common.RendererType.CustomHtml')}
                      </span>
                      {canSwitchRenderer && bookId && (
                        <button
                          type="button"
                          onClick={() => onRequestRendererSwitch(bookId)}
                          className="text-sm text-indigo-600 hover:text-indigo-700 underline min-h-[32px]"
                        >
                          {t('App.Admin.Books.BookEditDrawer.SwitchRendererLink')}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-neutral-500 mt-1">
                      {t('App.Admin.Books.BookEditDrawer.RendererTypeHint')}
                    </p>
                  </Field>
                  <Field label={t('App.Admin.Books.BookEditDrawer.FieldSubjects')}>
                    <input
                      className="border rounded px-3 py-2 min-h-[44px] w-full"
                      value={form.subjects ?? ''}
                      onChange={e => { setForm({ ...form, subjects: e.target.value || undefined }); setDirty(true); }}
                    />
                  </Field>
                  <Field label={t('App.Admin.Books.BookEditDrawer.FieldContributors')}>
                    <input
                      className="border rounded px-3 py-2 min-h-[44px] w-full"
                      value={form.contributors ?? ''}
                      onChange={e => { setForm({ ...form, contributors: e.target.value || undefined }); setDirty(true); }}
                    />
                  </Field>
                  <Field label={t('App.Admin.Books.BookEditDrawer.FieldRights')}>
                    <input
                      className="border rounded px-3 py-2 min-h-[44px] w-full"
                      value={form.rights ?? ''}
                      onChange={e => { setForm({ ...form, rights: e.target.value || undefined }); setDirty(true); }}
                    />
                  </Field>
                  <Field label={t('App.Admin.Books.BookEditDrawer.FieldComplexityScore')}>
                    <input
                      type="number" min={0}
                      className="border rounded px-3 py-2 min-h-[44px] w-full"
                      value={form.complexityScore ?? ''}
                      onChange={e => { setForm({ ...form, complexityScore: e.target.value === '' ? undefined : Number(e.target.value) }); setDirty(true); }}
                    />
                  </Field>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.isDrmEnabled}
                      onChange={e => { setForm({ ...form, isDrmEnabled: e.target.checked }); setDirty(true); }}
                    />
                    {t('App.Admin.Books.BookEditDrawer.DrmEnabled')}
                  </label>

                  {saveMetadata.isError && (
                    <div role="alert" className="mt-3 text-sm text-red-600 space-y-2">
                      <p>{(saveMetadata.error as Error).message}</p>
                      {isConcurrencyConflict(saveMetadata.error) && (
                        <button
                          type="button"
                          onClick={() => {
                            void qc.invalidateQueries({ queryKey: ['manage-book', bookId] });
                            setDirty(false);
                          }}
                          className="px-3 py-1 border border-red-300 rounded text-red-700 min-h-[36px]"
                        >
                          {t('App.Admin.Books.BookEditDrawer.ReloadLatest')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {tab === 'drm' && (
                <div role="tabpanel" className="space-y-3">
                  <p className="text-xs text-neutral-500">
                    {t('App.Admin.Books.BookEditDrawer.DrmTabHint')}
                  </p>
                  <label className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={form.isDrmEnabled}
                      onChange={e => { setForm({ ...form, isDrmEnabled: e.target.checked }); setDirty(true); }}
                    />
                    {t('App.Admin.Books.BookEditDrawer.DrmEnabled')}
                  </label>
                  <DrmRow label="Print"    limit={drm.defaultPrintQuotaLimit}    days={drm.defaultPrintQuotaWindowDays}
                          onLimit={v => { setDrm({ ...drm, defaultPrintQuotaLimit: v }); setDirty(true); }}
                          onDays={v => { setDrm({ ...drm, defaultPrintQuotaWindowDays: v }); setDirty(true); }}
                          labelLimit={t('App.Admin.Books.BookEditDrawer.DrmLimitLabel', { label: 'Print' })}
                          labelWindow={t('App.Admin.Books.BookEditDrawer.DrmWindowLabel', { label: 'Print' })}
                          placeholder={t('App.Admin.Books.BookEditDrawer.DrmPlaceholder')} />
                  <DrmRow label="Copy"     limit={drm.defaultCopyQuotaLimit}     days={drm.defaultCopyQuotaWindowDays}
                          onLimit={v => { setDrm({ ...drm, defaultCopyQuotaLimit: v }); setDirty(true); }}
                          onDays={v => { setDrm({ ...drm, defaultCopyQuotaWindowDays: v }); setDirty(true); }}
                          labelLimit={t('App.Admin.Books.BookEditDrawer.DrmLimitLabel', { label: 'Copy' })}
                          labelWindow={t('App.Admin.Books.BookEditDrawer.DrmWindowLabel', { label: 'Copy' })}
                          placeholder={t('App.Admin.Books.BookEditDrawer.DrmPlaceholder')} />
                  <DrmRow label="Download" limit={drm.defaultDownloadQuotaLimit} days={drm.defaultDownloadQuotaWindowDays}
                          onLimit={v => { setDrm({ ...drm, defaultDownloadQuotaLimit: v }); setDirty(true); }}
                          onDays={v => { setDrm({ ...drm, defaultDownloadQuotaWindowDays: v }); setDirty(true); }}
                          labelLimit={t('App.Admin.Books.BookEditDrawer.DrmLimitLabel', { label: 'Download' })}
                          labelWindow={t('App.Admin.Books.BookEditDrawer.DrmWindowLabel', { label: 'Download' })}
                          placeholder={t('App.Admin.Books.BookEditDrawer.DrmPlaceholder')} />
                  {saveDrm.isError && (
                    <p role="alert" className="text-sm text-red-600 mt-3">
                      {(saveDrm.error as Error).message}
                    </p>
                  )}
                </div>
              )}

              {tab === 'cover' && (
                <div role="tabpanel">
                  <BookCoverUploader
                    currentUrl={form.coverImageUrl}
                    onUpload={file => uploadCover.mutateAsync(file).then(() => undefined)}
                    busy={uploadCover.isPending}
                  />
                  {uploadCover.isError && (
                    <p role="alert" className="text-sm text-red-600 mt-3">
                      {(uploadCover.error as Error).message}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <footer className="border-t border-neutral-200 p-4 flex justify-between items-center">
          <span className="text-xs text-neutral-500">{dirty ? t('App.Admin.Books.BookEditDrawer.UnsavedChanges') : ''}</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={attemptClose}
              className="px-4 py-2 border rounded min-h-[44px]"
            >{t('App.Admin.Books.BookEditDrawer.Close')}</button>
            {tab === 'metadata' && (
              <button
                type="button"
                onClick={() => form && saveMetadata.mutate(form)}
                disabled={!form || saveMetadata.isPending || !dirty}
                className="px-4 py-2 bg-primary-600 text-white rounded disabled:opacity-50 min-h-[44px]"
              >{saveMetadata.isPending ? t('App.Admin.Books.BookEditDrawer.Saving') : t('App.Admin.Books.BookEditDrawer.SaveMetadata')}</button>
            )}
            {tab === 'drm' && (
              <button
                type="button"
                onClick={() => saveDrm.mutate()}
                disabled={saveDrm.isPending || !dirty}
                className="px-4 py-2 bg-primary-600 text-white rounded disabled:opacity-50 min-h-[44px]"
              >{saveDrm.isPending ? t('App.Admin.Books.BookEditDrawer.Saving') : t('App.Admin.Books.BookEditDrawer.SaveDrm')}</button>
            )}
          </div>
        </footer>
      </aside>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col text-sm">
      <span className="text-neutral-700">{label}</span>
      {children}
    </label>
  );
}

interface DrmRowProps {
  label: string;
  limit: string;
  days: string;
  onLimit: (v: string) => void;
  onDays: (v: string) => void;
  labelLimit: string;
  labelWindow: string;
  placeholder: string;
}

function isConcurrencyConflict(err: unknown): boolean {
  if (!err) return false;
  const anyErr = err as { response?: { data?: { error?: { code?: string; message?: string } } }; message?: string };
  const code = anyErr.response?.data?.error?.code;
  if (code && code.toLowerCase().includes('concurrencyconflict')) return true;
  const msg = (anyErr.response?.data?.error?.message ?? anyErr.message ?? '').toLowerCase();
  return msg.includes('modified by another user') || msg.includes('concurrencyconflict');
}

function DrmRow({ limit, days, onLimit, onDays, labelLimit, labelWindow, placeholder }: DrmRowProps) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <label className="flex flex-col text-sm">
        <span className="text-neutral-700">{labelLimit}</span>
        <input
          type="number" min={0} value={limit}
          onChange={e => onLimit(e.target.value)}
          placeholder={placeholder}
          className="border rounded px-3 py-2 min-h-[44px]"
        />
      </label>
      <label className="flex flex-col text-sm">
        <span className="text-neutral-700">{labelWindow}</span>
        <input
          type="number" min={0} value={days}
          onChange={e => onDays(e.target.value)}
          placeholder={placeholder}
          className="border rounded px-3 py-2 min-h-[44px]"
        />
      </label>
    </div>
  );
}
